import React, { createContext, useCallback, useContext, useMemo } from "react";
import Swal from "sweetalert2";

const ToastContext = createContext(null);

const normalizeToastInput = (input, type = "info", options = {}) => {
  if (typeof input === "string") {
    return {
      message: input,
      type,
      ...options,
    };
  }

  return {
    type: "info",
    ...input,
  };
};

const toastIconByType = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
};

export const ToastProvider = ({ children }) => {
  const showToast = useCallback((input, type, options) => {
    const normalized = normalizeToastInput(input, type, options);
    const duration =
      typeof normalized.duration === "number" ? normalized.duration : 3000;

    const title = normalized.title || normalized.message || "";
    const text =
      normalized.text ||
      (normalized.title && normalized.message ? normalized.message : undefined);

    return Swal.fire({
      toast: true,
      position: normalized.position || "top-end",
      icon: toastIconByType[normalized.type] || "info",
      title,
      text,
      showConfirmButton: false,
      timer: duration > 0 ? duration : undefined,
      timerProgressBar: normalized.timerProgressBar ?? true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
    });
  }, []);

  const success = useCallback(
    (message, options) => showToast(message, "success", options),
    [showToast],
  );
  const error = useCallback(
    (message, options) => showToast(message, "error", options),
    [showToast],
  );
  const warning = useCallback(
    (message, options) => showToast(message, "warning", options),
    [showToast],
  );
  const info = useCallback(
    (message, options) => showToast(message, "info", options),
    [showToast],
  );
  const dismissToast = useCallback(() => Swal.close(), []);

  const value = useMemo(
    () => ({
      showToast,
      success,
      error,
      warning,
      info,
      dismissToast,
    }),
    [showToast, success, error, warning, info, dismissToast],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider.");
  }
  return context;
};
